import React from 'react';
import Yup from 'yup';
import StaticData from '../../services/StaticData';
import UserService from '../../services/UserService';
import CommonDataService from '../../services/CommonDataService';
import Section from '../common/Section';
import SectionHeader from '../common/SectionHeader';
import Form from '../form';
import CancelButton from '../form/controls/CancelButton';
import { UserLanguagesField } from './UserLanguages';

const schema = Yup.object().shape({
  firstName: Yup
    .string()
    .required("First Name required"),
  lastName: Yup
    .string()
    .required("Last Name required"),
  email: Yup
    .string()
    .email("Invalid email")
    .required("Email required"),
  useSecondaryEmail: Yup
    .boolean(),
  secondaryEmail: Yup
    .string()
    .nullable()
    .email("Invalid email")
    .when('useSecondaryEmail', {
      is: true,
      then: Yup.string().required("Secondary email is required")
    }),
  officeLocation: Yup
    .object()
    .nullable()
    .test("selected", "Location is required", value => value && value.id > 0),
  projectRole: Yup
    .object()
    .nullable()
    .test("selected", "Project Role is required", value => value && value.id > 0),
  team: Yup
    .object()
    .nullable()
    .test("selected", "Team is required", value => value && value.id > 0),
  role: Yup
    .object()
    .nullable()
    .test("selected", "Application Role is required", value => value && value.id > 0),
  availability: Yup
    .object()
    .nullable()
    .test("selected", "Availability is required", value => value && value.id > 0),
  minHours: Yup
    .number()
    .required("Minimum hours required")
    .positive()
    .integer()
    .moreThan(0, "Minimum hours must be greater than 0"),
  maxHours: Yup
    .number()
    .required("Maximun hours required")
    .positive()
    .integer()
    .moreThan(0, "Maximun hours must be greater than 0"),
  skillLevel: Yup
    .number()
    .positive()
    .integer()
    .test("selected", "Skill level is required", value => value && value > 0),
  languages: Yup
    .array()
    .of(Yup.object())
    .min(1, "Languages must have at least 1 item")
})

class UserEdit extends React.Component {
  constructor(props) {
    super(props);

    this.saveChanges = this.saveChanges.bind(this);
    this.delete = this.delete.bind(this);

    this.state = {
      user: {
        id: 0,
        type: "",
        attributes: {
          firstName: "",
          lastName: "",
          email: "",
          secondaryEmail: "",
          useSecondaryEmail: false,
          officeLocation: {},
          projectRole: {},
          teamLead: false,
          team: {},
          availability: {},
          languages: [],
          minHours: 0,
          maxHours: 0,
          cost: 0,
          skillLevel: 0,
          workingHours: ""
        }
      },
      projectRoles: [],
      roles: [],
      locations: [],
      languages: [],
      teams: [],
      availability: []
    };
  }

  componentDidMount() {

    CommonDataService
      .getProjectRoles()
      .then(projectRoles => this.setState({ projectRoles }));

    CommonDataService
      .getLocations()
      .then(locations => this.setState({ locations }));

    CommonDataService
      .getTeams()
      .then(teams => this.setState({ teams }));

    CommonDataService
      .getAvailabilityTypes()
      .then(availability => this.setState({ availability }));

    CommonDataService
      .getRoles()
      .then(roles => this.setState({ roles }));

    const id = this.props.match.params.id;

    if (id && id > 0) {
      UserService.getById(id).then(result => {
        this.setState({ user: Object.assign({}, result) });
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.user.id !== nextProps.user.id) {
      this.setState({ user: Object.assign({}, nextProps.user) });
    }
  }

  delete() {
  }

  saveChanges(values) {

    const current = { ...this.state.user };
    Object.assign(current.attributes, values);

    if (current.id > 0) {
      return UserService.save(current);
    }

    return UserService.create(current);
  }

  render() {
    return (
      <React.Fragment>
        <SectionHeader title="Edit">
          <a className="delete" onClick={this.delete} title="Delete" data-icon="&#xe912;">Delete</a>
        </SectionHeader>
        <Section className="edit-page">
          <Form
            initialValues={{ ...this.state.user.attributes }}
            enableReinitialize={true}
            validationSchema={schema}
            redirect={this.props.returnPath}
            onSubmit={this.saveChanges}>

            <Form.Errors />

            {this.state.user.id > 0 && (<h2>ID {this.state.user.id}</h2>)}

            <Form.FieldGroup label="First Name*">
              <Form.Input
                name="firstName"
                placeholder="First name" />
              <Form.FieldError name="firstName" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Last Name*">
              <Form.Input
                name="lastName"
                placeholder="Last name" />
              <Form.FieldError name="lastName" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Email*">
              <Form.Input
                name="email"
                placeholder="Email" />
              <Form.FieldError name="email" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Secondary Email">
              <Form.Input
                name="secondaryEmail"
                placeholder="Secondary Email" />
              <Form.Input
                name="useSecondaryEmail"
                label="Send notifications to secondary email"
                type="checkbox" />
              <Form.FieldError name="secondaryEmail" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Office Location*">
              <Form.Dropdown
                name="officeLocation"
                options={this.state.locations}
                multi={false} />
              <Form.FieldError name="officeLocation" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Project Role*">
              <Form.Dropdown
                name="projectRole"
                options={this.state.projectRoles}
                multi={false} />
              <Form.FieldError name="projectRole" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Team Lead">
              <Form.Input
                name="teamLead"
                type="checkbox" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Team*">
              <Form.Dropdown
                name="team"
                options={this.state.teams}
                multi={false}
                valueKey="id"
                labelKey="name" />
              <Form.FieldError name="team" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Application Role*">
              <Form.Dropdown
                name="role"
                options={this.state.roles}
                multi={false} />
              <Form.FieldError name="role" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Languages*">
              <UserLanguagesField name="languages" />
              <Form.FieldError name="languages" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Availability*">
              <Form.Dropdown
                name="availability"
                options={this.state.availability} />
              <Form.FieldError name="availability" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Minimum hour limit*" className="right to-right">
              <Form.Input
                name="minHours"
                step="1"
                type="number"
                placeholder="0.0"
                className="max-320" />
              <Form.FieldError name="minHours" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Maximum hour limit*" className="right to-right">
              <Form.Input
                name="maxHours"
                step="1"
                type="number"
                placeholder="0.0"
                className="max-320" />
              <Form.FieldError name="maxHours" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Working hours" className="right to-right">
              <Form.Input
                name="workingHours"
                placeholder="0-24"
                className="max-320" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Skill Levels*">
              <Form.RadioButton
                name="skillLevel"
                options={StaticData.Complexity} />
              <Form.FieldError name="skillLevel" />
            </Form.FieldGroup>

            <Form.FieldGroup label="Cost of resource" className="right to-right">
              <Form.Input
                name="cost"
                step="0.1"
                type="number"
                placeholder="0.0"
                className="max-320" />
            </Form.FieldGroup>

            <fieldset className="buttons-container">
              <button type="submit">Save</button>
              <CancelButton path={this.props.returnPath} />
            </fieldset>
          </Form>
        </Section>
      </React.Fragment>
    );
  }
}

UserEdit.defaultProps = {
  returnPath: "/staff"
}

export default UserEdit;



// WEBPACK FOOTER //
// ./src/components/users/UserEdit.js